// ============================================
// 格式化工具
// ============================================

export type DateFormatOptions = {
  /** BCP 47 locale; default from configureFormatDefaults or en-AU */
  locale?: string;
  /**
   * IANA time zone (e.g. Australia/Sydney).
   * Omit / undefined = browser local timezone.
   */
  timeZone?: string;
};

type FormatDefaults = {
  locale: string;
  timeZone?: string;
};

let formatDefaults: FormatDefaults = {
  locale: 'en-AU',
};

/** Set process-wide defaults (Admin syncs language + display timezone here). */
export function configureFormatDefaults(next: Partial<FormatDefaults>): void {
  const merged: FormatDefaults = {
    locale: next.locale ?? formatDefaults.locale,
  };
  if (Object.prototype.hasOwnProperty.call(next, 'timeZone')) {
    merged.timeZone = next.timeZone;
  } else {
    merged.timeZone = formatDefaults.timeZone;
  }
  formatDefaults = merged;
}

export function getFormatDefaults(): FormatDefaults {
  return { ...formatDefaults };
}

function resolveOpts(opts?: DateFormatOptions): Required<Pick<DateFormatOptions, 'locale'>> &
  Pick<DateFormatOptions, 'timeZone'> {
  return {
    locale: opts?.locale ?? formatDefaults.locale,
    timeZone: opts?.timeZone ?? formatDefaults.timeZone,
  };
}

/** Calendar day YYYY-MM-DD → local noon-free Date without UTC day-shift. */
function parseInputDate(date: string | Date): Date {
  if (date instanceof Date) return date;
  const dayOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (dayOnly) {
    return new Date(Number(dayOnly[1]), Number(dayOnly[2]) - 1, Number(dayOnly[3]));
  }
  return new Date(date);
}

function tzOpts(timeZone?: string): { timeZone?: string } {
  return timeZone ? { timeZone } : {};
}

/** Calendar day — year/month/day only (business service dates, expiries). */
export function formatDate(date: string | Date, opts?: DateFormatOptions): string {
  const d = parseInputDate(date);
  if (Number.isNaN(d.getTime())) return '—';
  const { locale, timeZone } = resolveOpts(opts);
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...tzOpts(timeZone),
  });
}

/**
 * Instant — date + time to the minute (SOS, feedback, audit timestamps).
 * Default zone is browser local unless `timeZone` / defaults set.
 */
export function formatDateTime(date: string | Date, opts?: DateFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  const { locale, timeZone } = resolveOpts(opts);
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...tzOpts(timeZone),
  });
}

/** Clock time on a schedule day (pickup_time HH:mm:ss). */
export function formatTime(time: string | Date, opts?: DateFormatOptions): string {
  const d = typeof time === 'string' ? new Date(`1970-01-01T${time}`) : time;
  if (Number.isNaN(d.getTime())) return '—';
  const { locale, timeZone } = resolveOpts(opts);
  return d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: locale.startsWith('en'),
    ...tzOpts(timeZone),
  });
}

export function formatPhone(phone: string): string {
  if (phone.startsWith('+61')) {
    const local = '0' + phone.substring(3);
    return local.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  return phone;
}

export function formatCapacity(capacity: number): string {
  return `${capacity} seats`;
}

export function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    scheduled: 'Scheduled',
    dispatched: 'Dispatched',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    active: 'Active',
    maintenance: 'Under Maintenance',
    retired: 'Retired',
    inactive: 'Inactive',
    new: 'New',
    handling: 'Handling',
    resolved: 'Resolved',
    unscheduled: 'Pending schedule',
    planning: 'Planning',
    pending_owner: 'Pending owner',
    accepted: 'Accepted',
    rejected: 'Rejected',
    reviewed: 'Reviewed',
    available: 'Available',
    busy: 'Busy',
    pending_decision: 'Pending owner',
  };
  return statusMap[status] || status;
}

export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    scheduled: 'blue',
    dispatched: 'cyan',
    in_progress: 'processing',
    completed: 'success',
    cancelled: 'error',
    active: 'success',
    maintenance: 'warning',
    retired: 'default',
    inactive: 'default',
    new: 'error',
    handling: 'processing',
    resolved: 'success',
    unscheduled: 'default',
    planning: 'processing',
    pending_owner: 'gold',
    accepted: 'success',
    rejected: 'error',
    reviewed: 'success',
    available: 'success',
    busy: 'processing',
    pending_decision: 'gold',
  };
  return colorMap[status] || 'default';
}

export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

export function formatETA(durationSeconds: number): string {
  if (durationSeconds < 60) {
    return 'Less than 1 min';
  }
  const minutes = Math.ceil(durationSeconds / 60);
  if (minutes < 60) {
    return `${minutes} min${minutes > 1 ? 's' : ''}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hr ${remainingMinutes} min`;
}

type GeoPointInput =
  | string
  | { type?: string; coordinates?: number[] }
  | null
  | undefined;

/** Decode PostGIS EWKB hex returned by PostgREST for geography columns. */
function parseEwkbHex(hex: string): { lat: number; lng: number } | null {
  if (!/^[0-9a-fA-F]+$/.test(hex) || hex.length < 42) return null;

  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.slice(i, i + 2), 16);
  }
  if (bytes.length < 21) return null;

  const view = new DataView(bytes.buffer);
  const littleEndian = bytes[0] === 1;
  let offset = 1;
  const geomType = view.getUint32(offset, littleEndian);
  offset += 4;
  if (geomType & 0x20000000) offset += 4;

  const lng = view.getFloat64(offset, littleEndian);
  offset += 8;
  const lat = view.getFloat64(offset, littleEndian);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/** Parse PostGIS WKT, EWKT, EWKB hex, or GeoJSON Point from Supabase geography columns. */
export function parseGeoPoint(value: GeoPointInput): { lat: number; lng: number } | null {
  if (value == null) return null;

  if (typeof value === 'object' && value.type === 'Point' && Array.isArray(value.coordinates)) {
    const [lng, lat] = value.coordinates;
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return null;
  }

  const raw = typeof value === 'string' ? value : JSON.stringify(value);

  if (/^[0-9a-fA-F]+$/.test(raw)) {
    const fromEwkb = parseEwkbHex(raw);
    if (fromEwkb) return fromEwkb;
  }

  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw) as { type?: string; coordinates?: number[] };
      if (parsed.type === 'Point' && Array.isArray(parsed.coordinates)) {
        const [lng, lat] = parsed.coordinates;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }
    } catch {
      // fall through to WKT parsing
    }
  }

  const match = raw.match(/POINT\(([^ ]+) ([^ )]+)\)/);
  if (match) {
    return {
      lng: parseFloat(match[1]),
      lat: parseFloat(match[2]),
    };
  }
  return null;
}

export function formatTrackTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export function formatTrackTimeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function toGeoPointWKT(lat: number, lng: number): string {
  return `SRID=4326;POINT(${lng} ${lat})`;
}

/** Hex colors for antd-mobile Tag (driver H5). */
export function buildGoogleMapsNavUrl(address: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}
